import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile, useOrganizationAlias } from "@domain/currentProfile/hooks";
import { PostComment } from "@domain/post/comment/postComment";
import { Person } from "@domain/user/user";
import { fullName } from "@domain/user/utils";
import { useAsync } from "@utils/hooks";
import { gap } from "@ui/styles/utils";
import { useAuthModalBlocker } from "@ui/components/auth/useAuthModalBlocker";
import { SendIcon } from "@ui/components/icons";
import { RichTextInput, RichTextInputRef } from "@ui/components/inputs/draft/richTextInput";
import { createMentionEntities } from "@ui/components/inputs/draft/useDraftPlugins";
import { row } from "@ui/components/layout";
import { ProfilePicture } from "@ui/components/profile/profilePicture";

export interface CommentInputRef {
	focus: () => void;
}
interface CommentInputProps extends React.HTMLAttributes<HTMLDivElement> {
	postId: string;
	onCommentSubmit: (comment?: PostComment) => void;
	placeholder?: string;
	fixed?: boolean;
	initialComment?: PostComment;
	replyTo?: Person;
	parentId?: string;
}
export const CommentInput = forwardRef<CommentInputRef, CommentInputProps>(
	({ onCommentSubmit, fixed, postId, initialComment, placeholder, replyTo, parentId, ...htmlProps }, ref) => {
		const currentProfile = useCurrentProfile();
		const { postService, trackingService } = useServices();
		const containerRef = useRef<HTMLDivElement>(null);
		const alias = useOrganizationAlias();
		const inputRef = useRef<RichTextInputRef>(null);
		const blockGuest = useAuthModalBlocker();
		const mentionedFullName = replyTo
			? fullName({ primaryName: replyTo.primaryName, secondaryName: replyTo.secondaryName })
			: "";

		const [text, setText] = useState(
			initialComment?.text
				? initialComment.text
				: replyTo && replyTo.id !== currentProfile?.id
					? createMentionEntities(mentionedFullName + " ", [
							{
								mention: {
									id: replyTo.id,
									name: mentionedFullName,
									type: replyTo.type,
									slug: replyTo.slug,
								},
							},
						])
					: ContentState.createFromText("")
		);

		const { run: submit, loading } = useAsync(async () => {
			if (!text.getPlainText().trim()) {
				return;
			}
			const comment = await postService.comment(postId, text, initialComment?.id, parentId);
			onCommentSubmit(comment);
			inputRef.current?.empty();
			trackingService.track("Feed - commented post", {
				user: currentProfile?.primaryName,
				contentType: postId,
			});
		}, [text, onCommentSubmit, postId, initialComment?.id, parentId]);

		useImperativeHandle(ref, () => ({
			focus: () => {
				inputRef.current?.focus();
			},
		}));

		return (
			<Container {...htmlProps} ref={containerRef} loading={loading}>
				<ProfilePicture
					size={initialComment ? 36 : "S"}
					picture={alias ? alias.avatar : currentProfile?.avatar}
					fallbackType={alias ? alias.type : "USER"}
				/>
				<StyledInput
					readOnly={loading}
					mentionsEnabled
					toolbarOffset={containerRef.current?.getBoundingClientRect().height}
					ref={inputRef}
					value={text}
					onChange={(v) => setText(v)}
					onEnterKey={(e) => {
						if (!e.shiftKey) {
							e.preventDefault();
							blockGuest(() => {
								submit();
							});
							return true;
						}
						return false;
					}}
					placeholder={placeholder ?? I18n.t("post.comment.input.placeholder")}
					richStyleEnabled={false}
					initialMention={replyTo}
					autoFocus={!!replyTo}
				/>
				<SendIconContainer onClick={() => blockGuest(() => submit())} disabled={!text.getPlainText().trim() || loading}>
					<SendIcon />
				</SendIconContainer>
			</Container>
		);
	}
);

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "loading",
})<{ loading: boolean }>`
	display: flex;
	align-items: center;

	${gap(10, "row")}

	> svg {
		width: 16px;
		height: 16px;
		path {
			fill: ${({ theme }) => theme.colors.primary};
		}
	}

	${({ loading }) =>
		loading &&
		css`
			opacity: 0.6;
			pointer-events: none;
		`}
`;

const StyledInput = styled(RichTextInput)`
	flex: 1;
	flex-shrink: 1;
	border: 1px solid ${({ theme }) => theme.colors.lightergray};
	border-radius: 16px;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 10px 12px 9px;
		font-size: 0.8125rem;
	}
	.public-DraftEditorPlaceholder-root {
		font-weight: 400;
		opacity: 0.8;
	}
`;
const SendIconContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled: boolean }>`
	cursor: pointer;
	transition: opacity 0.1s linear;
	${row(0, "center", "center")};
	width: 30px;
	height: 30px;
	border-radius: 15px;
	background-color: ${({ theme }) => theme.colors.secondary};
	${({ disabled }) =>
		disabled &&
		css`
			cursor: initial;
			opacity: 0.6;
		`}
	> svg {
		path {
			fill: ${({ theme }) => theme.colors.white};
		}
	}
`;
